import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  BellRing,
  Heart,
  Layers,
  MessageCircleHeart,
  Sparkles,
  SunMedium,
  Ticket,
  type LucideIcon,
} from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { useEntitlements } from '../../hooks/use-entitlements'
import { useSkuPrice } from '../../hooks/use-catalog'
import { detectPlatformAdapter } from '../../platform'
import { usePaywallStore } from '../../store/paywall.store'
import { Button } from '../ui/Button'
import { Modal } from '../ui/Modal'
import { PromoCodeModal } from '../promo/PromoCodeModal'

const adapter = detectPlatformAdapter()

/**
 * Single-CTA premium pitch. Lists what's inside the subscription, leads with
 * one big "Subscribe" button. No yearly, no one-time fallback — post-Phase-2
 * everything except `sub_monthly` is disabled in the catalog. If we ever
 * re-enable yearly (Phase 5), add a second tile underneath the CTA.
 */
export function PaywallModal() {
  const { t } = useTranslation()
  const { open, ctx, hide } = usePaywallStore()
  const [error, setError] = useState<string | null>(null)
  const [promoOpen, setPromoOpen] = useState(false)
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; percent: number } | null>(null)
  // When true, useEntitlements polls every 3s. Flipped on as soon as the
  // user taps Subscribe / Open in Telegram, flipped back off either when
  // entitlements report Premium (success) or when the modal closes.
  const [awaitingPayment, setAwaitingPayment] = useState(false)
  const queryClient = useQueryClient()
  const entQ = useEntitlements({ fastPolling: awaitingPayment })
  // Live price — admins change it from the admin UI without a redeploy. Falls
  // back to a sensible Stars number while the catalog is loading so the
  // button doesn't render with an empty value.
  const subPrice = useSkuPrice('sub_monthly')
  const priceStars = subPrice?.priceStars ?? 300

  // Trial eligibility — only show the trial CTA for first-time users.
  const trialQ = useQuery({
    queryKey: ['me', 'trial-status'],
    queryFn: () => api.trialStatus(),
    staleTime: 30_000,
  })
  const trialEligible = trialQ.data?.eligible === true

  const trialMutation = useMutation({
    mutationFn: () => api.trialStart(),
    onSuccess: () => {
      toast.success(t('paywall.trial_activated'))
      // Refresh entitlements so isPremium flips on immediately.
      void queryClient.invalidateQueries({ queryKey: ['entitlements'] })
      void queryClient.invalidateQueries({ queryKey: ['me', 'trial-status'] })
      hide()
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.body.error.message : (err as Error).message)
    },
  })

  // The moment entitlements come back as Premium while we're awaiting payment,
  // close the paywall and celebrate. Without this the user would sit on the
  // paywall watching nothing happen for 30+ seconds even though TG already
  // delivered `successful_payment` to the bot.
  useEffect(() => {
    if (!awaitingPayment) return
    if (entQ.data?.isPremium) {
      setAwaitingPayment(false)
      toast.success(t('paywall.payment_received'))
      // Bust any cached "you don't have access" responses for the feature
      // the user was trying to reach.
      void queryClient.invalidateQueries()
      hide()
    }
  }, [awaitingPayment, entQ.data?.isPremium, hide, queryClient, t])

  // Stop polling fast when the modal is dismissed without payment.
  useEffect(() => {
    if (!open && awaitingPayment) setAwaitingPayment(false)
  }, [open, awaitingPayment])

  const invoiceMutation = useMutation({
    mutationFn: () =>
      api.createInvoice({
        sku: 'sub_monthly',
        promoCode: appliedPromo?.code,
      }),
    onSuccess: async (invoice) => {
      try {
        // openInvoice (TG ≥ 6.1) shows the native invoice modal AND awaits
        // its terminal status. On older clients / MAX it falls through to
        // openLink and returns 'pending', so we still poll like before.
        const status = await adapter.openInvoice(invoice.payUrl)
        if (status === 'paid') {
          // TG just signed off on the charge — our bot webhook is racing
          // with our entitlements refresh. Bump the polling loop.
          setAwaitingPayment(true)
        } else if (status === 'cancelled') {
          // User dismissed — no action needed; nothing to clean up
          // since we never wrote a Purchase row for subscriptions.
        } else if (status === 'failed') {
          setError(t('paywall.payment_failed'))
        } else {
          // 'pending' — older clients / MAX fallback path. Same polling
          // behaviour as the old openLink flow.
          setAwaitingPayment(true)
        }
      } catch (err) {
        setError((err as Error).message)
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.body.error.message : (err as Error).message)
    },
  })

  // MAX runs without a payment provider in v1 — all purchases happen in TG via
  // Stars. We re-route the paywall: tap the CTA → generate a link code, open
  // the TG bot with `?startapp=link_<code>`. On first TG auth the accounts
  // merge, the user sees the TG paywall, pays, and Premium reflects back on MAX.
  const isMax = adapter.platform === 'max'
  const tgRedirectMutation = useMutation({
    mutationFn: () => api.startLink(),
    onSuccess: async (link) => {
      try {
        if (link.deeplinkTelegram) {
          await adapter.openLink(link.deeplinkTelegram)
        } else {
          setError(t('paywall.max_no_tg_configured'))
        }
      } catch (err) {
        setError((err as Error).message)
      }
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.body.error.message : (err as Error).message)
    },
  })

  if (!open) return null

  // Special case: user is on a free trial AND ran into natal_full
  // (which trial doesn't unlock). Without distinguishing this, the
  // generic "Become Premium" copy reads as a contradiction — they
  // already have Premium status from the trial. Give them the precise
  // explanation: trial covers everything EXCEPT this one feature.
  const isTrialNatalLock =
    entQ.data?.provider === 'trial' && ctx?.feature === 'natal_full'

  const reasonText = isTrialNatalLock
    ? t('paywall.trial_natal_locked_eyebrow')
    : ctx?.reason === 'free_quota_exhausted'
      ? t('paywall.free_quota_exhausted')
      : ctx?.reason === 'expired'
        ? t('paywall.expired')
        : t('paywall.feature_locked')

  const headlineText = isTrialNatalLock
    ? t('paywall.trial_natal_locked_headline')
    : t('paywall.headline')

  const features: Array<{ Icon: LucideIcon; key: string }> = [
    { Icon: SunMedium, key: 'feat_personal' },
    { Icon: BellRing, key: 'feat_alerts' },
    { Icon: MessageCircleHeart, key: 'feat_chat' },
    { Icon: Layers, key: 'feat_tarot' },
    { Icon: Sparkles, key: 'feat_natal' },
    { Icon: Heart, key: 'feat_compat' },
  ]

  return (
    <Modal open={open} onClose={hide}>
      <div className="flex flex-col gap-3">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-widest text-sage-500">
            {isTrialNatalLock ? reasonText : (ctx?.message ?? reasonText)}
          </p>
          <h2 className="mt-0.5 font-serif text-lg text-sage-800">
            {headlineText}
          </h2>
          {isTrialNatalLock && (
            <p className="mt-1.5 text-xs leading-snug text-sage-600">
              {t('paywall.trial_natal_locked_body')}
            </p>
          )}
        </div>

        <ul className="grid grid-cols-2 gap-1.5">
          {features.map(({ Icon, key }) => (
            <li
              key={key}
              className="flex items-start gap-2 rounded-xl bg-white/70 p-2 ring-1 ring-sage-200/60"
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-copper" strokeWidth={1.6} aria-hidden />
              <span className="text-xs leading-snug text-sage-800">
                {t(`paywall.${key}`)}
              </span>
            </li>
          ))}
        </ul>

        {awaitingPayment && (
          <div className="rounded-xl bg-sage-50 px-3 py-2 text-center text-xs text-sage-800 ring-1 ring-sage-200/80">
            {t('paywall.awaiting_payment')}
          </div>
        )}

        <div className="flex flex-col gap-2">
          {isMax ? (
            <>
              <p className="rounded-xl bg-amber-50 px-3 py-1.5 text-center text-[11px] leading-snug text-amber-900 ring-1 ring-amber-200">
                {t('paywall.max_only_via_tg')}
              </p>
              <Button
                variant="primary"
                size="lg"
                loading={tgRedirectMutation.isPending}
                onClick={() => tgRedirectMutation.mutate()}
              >
                {t('paywall.max_open_tg_cta')}
              </Button>
              <p className="text-center text-[11px] leading-snug text-sage-500">
                {t('paywall.max_link_hint')}
              </p>
            </>
          ) : (
            <>
              {trialEligible && !appliedPromo && (
                <Button
                  variant="primary"
                  size="lg"
                  loading={trialMutation.isPending}
                  onClick={() => trialMutation.mutate()}
                >
                  <span className="flex flex-col items-center leading-tight">
                    <span>{t('paywall.trial_cta_title')}</span>
                    <span className="text-sm font-normal opacity-90">
                      {t('paywall.trial_cta_sub')}
                    </span>
                  </span>
                </Button>
              )}
              <Button
                variant={trialEligible && !appliedPromo ? 'secondary' : 'primary'}
                size="lg"
                loading={invoiceMutation.isPending}
                onClick={() => invoiceMutation.mutate()}
              >
                {appliedPromo ? (
                  t('paywall.subscribe_cta_discount', { percent: appliedPromo.percent })
                ) : (
                  <span className="flex flex-col items-center leading-tight">
                    <span>{t('paywall.subscribe_cta_title')}</span>
                    <span className="text-sm font-normal opacity-90">
                      {t('paywall.subscribe_cta_price', { stars: priceStars })}
                    </span>
                  </span>
                )}
              </Button>

              <div className="flex items-center justify-center gap-3 text-[11px] text-sage-500">
                {appliedPromo ? (
                  <span className="text-[#5b9d6e]">
                    {t('paywall.promo_applied', { code: appliedPromo.code, percent: appliedPromo.percent })}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setPromoOpen(true)}
                    className="flex items-center gap-1 text-sage-600 underline-offset-2 hover:text-sage-800 hover:underline"
                  >
                    <Ticket className="h-3 w-3" strokeWidth={1.6} aria-hidden />
                    {t('paywall.have_promo')}
                  </button>
                )}
                <span className="text-sage-300">·</span>
                <button
                  type="button"
                  onClick={hide}
                  className="text-sage-600 underline-offset-2 hover:text-sage-800 hover:underline"
                >
                  {t('actions.later')}
                </button>
              </div>

              <p className="text-center text-[10px] leading-tight text-sage-400">
                {t('paywall.disclaimer')}
              </p>
            </>
          )}

          {error && <p className="text-center text-xs text-copper">{error}</p>}

          {isMax && (
            <Button variant="ghost" size="sm" onClick={hide}>
              {t('actions.later')}
            </Button>
          )}
        </div>
      </div>

      <PromoCodeModal
        open={promoOpen}
        onClose={() => setPromoOpen(false)}
        onRedeemed={(result) => {
          if (result.kind === 'entitlement_period') {
            // Entitlement code — user is now premium; close everything.
            hide()
          } else if (result.kind === 'discount' && result.code && result.discountPercent) {
            setAppliedPromo({ code: result.code, percent: result.discountPercent })
          }
        }}
      />
    </Modal>
  )
}
